/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { styleguideSpacing } from '@kaiizen/kai-shiki/core';
import {
  MODAL_BASE_LINK_BUTTON_STYLE,
  MODAL_BASE_PRIMARY_BUTTON_STYLE,
  MODAL_BASE_SECONDARY_BUTTON_STYLE,
  ModalBase,
} from './ModalBase';

const MESSAGE_STYLE = css`
  label: CONFIRM_ACTION_DIALOG_MESSAGE_STYLE;
  color: ${styleguideColors.textSecondary};
  margin: 0;
`;

/** Cancel/Confirm stick to the right (ModalBase's ACTIONS_STYLE); pushing this
 *  group right via its own margin, rather than changing that justify-content,
 *  is what leaves the alternate action free to sit at the left of the same row. */
const ACTIONS_TRAILING_GROUP_STYLE = css`
  label: CONFIRM_ACTION_DIALOG_ACTIONS_TRAILING_GROUP_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s2}px;
  margin-left: auto;
`;

export const ConfirmActionDialog = ({
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  /** A less common way to resolve the dialog — rendered as a de-emphasized
   *  link at the left of the action row, not a third button competing with
   *  Confirm/Cancel for the same visual weight. */
  alternateLabel,
  busy = false,
  // Separate from `busy`: `busy` also disables Cancel (mid-mutation, closing
  // would be unsafe), while confirmDisabled only gates Confirm - e.g. while
  // some precondition the action needs isn't met yet, but the dialog is
  // otherwise safe to dismiss.
  confirmDisabled = false,
  onConfirm,
  onCancel,
  onAlternate,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  alternateLabel?: string;
  busy?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onAlternate?: () => void;
}) => (
  <ModalBase
    title={title}
    onOverlayClick={onCancel}
    actions={
      <>
        {alternateLabel && onAlternate && (
          <button type='button' css={MODAL_BASE_LINK_BUTTON_STYLE} disabled={busy} onClick={onAlternate}>
            {alternateLabel}
          </button>
        )}
        <div css={ACTIONS_TRAILING_GROUP_STYLE}>
          <button type='button' css={MODAL_BASE_SECONDARY_BUTTON_STYLE} disabled={busy} onClick={onCancel}>{cancelLabel}</button>
          <button type='button' css={MODAL_BASE_PRIMARY_BUTTON_STYLE} disabled={busy || confirmDisabled} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </>
    }
  >
    <p css={MESSAGE_STYLE}>{message}</p>
  </ModalBase>
);
